import { Modal, Popover } from 'antd'
import Images from 'src/common/Images'
import { useEffect, useRef, useState } from 'react'
import { CONFIG_URL, DATE_TIME_FORMAT_CLIENT_NEW, NOTI_SIZE, SOCKET_EVENT, getNoti } from 'src/helpers/constants'
import InfiniteScroll from 'react-infinite-scroll-component'
import { observer } from 'mobx-react-lite'
import api from 'src/common/api'
import { Request2 } from 'src/common/Request2'
import { isEmpty } from 'lodash'
import { ReactMarkdown } from 'react-markdown/lib/react-markdown'
import moment from 'moment'
import { socket } from 'src/helpers/socket/socket'
import { useStore } from 'src/hooks'
import NoData from 'src/common/components/NoData'
import styled from 'styled-components'

const NotificationItem = styled.li<{ isRead: boolean }>`
   background-color: ${(props) => (props.isRead ? '#fff' : '#f0f7ff')};
   padding: 12px 16px;
   cursor: pointer;
   border-bottom: 1px solid #f0f0f0;
   transition: background-color 0.2s;
   &:hover {
      background-color: ${(props) => (props.isRead ? '#fafafa' : '#e6f2ff')};
   }
`

const NotificationTitle = styled.div`
   font-weight: 600;
   font-size: 13px;
   color: #1a1a1a;
   margin-bottom: 2px;
   display: -webkit-box;
   -webkit-line-clamp: 1;
   -webkit-box-orient: vertical;
   overflow: hidden;
`

const NotificationDesc = styled.div`
   font-size: 13px;
   line-height: 18px;
   color: #444;
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
   p {
      margin: 0;
   }
`

const NotificationTime = styled.span`
   color: #999;
   font-size: 11px;
   margin-top: 4px;
   display: block;
`

const UnreadDot = styled.div`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background-color: #1890ff;
   flex-shrink: 0;
   margin-top: 6px;
`

const UserNotification = observer(() => {
   const AuthStore = useStore('AuthStore')
   const { data_getUserInfo } = AuthStore
   const currentPage = useRef<number>(0)
   const [jobs, setJobs] = useState({
      rows: [],
      count: 0,
      offset: 0
   }) as any
   const [isPopoverVisible, setPopoverVisible] = useState(false)
   const [isNewNotif, setIsNewNotif] = useState(false)
   const [detailVisible, setDetailVisible] = useState(false)
   const [selectedNotification, setSelectedNotification] = useState<any>(null)

   const handleVisibleChange = (visible: boolean) => {
      setPopoverVisible(visible)
   }

   const getListData = async (param?: any, pageNumber: number = 0) => {
      try {
         const offset = getNoti(pageNumber)
         const params = { offset, ...param }
         const result = await Request2.getWithToken(api.notification.get, { ...params, ...{ limit: NOTI_SIZE } })
         if (!isEmpty(result)) {
            if (params?.offset !== 0 && params?.offset !== jobs.offset) {
               setJobs({ ...result, rows: jobs?.rows.concat(result?.rows) })
            } else {
               setJobs(result)
            }
         }
      } catch (error) {
         // console.log(error)
      }
   }

   useEffect(() => {
      const handleNotification = (data: any) => {
         const isDirectMessage = data.toUserId === data_getUserInfo.id
         const isRoleMessage = data.toRole === data_getUserInfo.role
         if (isDirectMessage || isRoleMessage) {
            setIsNewNotif(true)
         }
      }

      socket.on(SOCKET_EVENT.NOTIFICATION, handleNotification)

      return () => {
         socket.off(SOCKET_EVENT.NOTIFICATION, handleNotification)
      }
   }, [data_getUserInfo.id, data_getUserInfo.role])

   // Check thông báo chưa đọc khi vào trang
   useEffect(() => {
      const checkUnread = async () => {
         try {
            const result = await Request2.getWithToken(api.notification.unread_count)
            if (result?.unreadCount > 0) {
               setIsNewNotif(true)
            }
         } catch (error) {
            // ignore
         }
      }
      if (data_getUserInfo?.id) {
         checkUnread()
      }
   }, [data_getUserInfo?.id])

   // Tự động hiển thị thông báo warning mới nhất (tối đa 3 lần)
   useEffect(() => {
      const autoShowWarningNotification = async () => {
         try {
            // Chỉ lấy đúng 1 thông báo warning mới nhất
            const result = await Request2.getWithToken(api.notification.get, {
               offset: 0,
               limit: 1,
               type: 'warning'
            })
            const latestWarning = result?.rows?.[0]
            if (!latestWarning) return

            // Nếu đã hết hạn thì không hiển thị popup
            if (latestWarning.expiresAt && moment(latestWarning.expiresAt).isSameOrBefore(moment())) {
               return
            }

            // Kiểm tra số lần đã hiển thị qua localStorage
            const storageKey = `warning_noti_shown_${latestWarning.id}`
            const shownCount = parseInt(localStorage.getItem(storageKey) || '0', 10)

            if (shownCount >= 3) return

            // Hiển thị thông báo và tăng bộ đếm
            localStorage.setItem(storageKey, String(shownCount + 1))
            setSelectedNotification(latestWarning)
            setDetailVisible(true)
         } catch (error) {
            // ignore
         }
      }

      if (data_getUserInfo?.id) {
         autoShowWarningNotification()
      }
   }, [data_getUserInfo?.id])

   const handleFetchJobs = () => {
      getListData({}, currentPage.current + 1)
      currentPage.current = currentPage.current + 1
   }

   const handlePopoverClick = () => {
      setIsNewNotif(false)
      currentPage.current = 0
      getListData({}, 0)
   }

   const handleItemClick = async (item: any) => {
      // Đánh dấu đã đọc
      try {
         await Request2.postWithToken({}, api.notification.mark_read + `/${item.id}`)
         const updatedRows = jobs.rows.map((row: any) => (row.id === item.id ? { ...row, markRead: true } : row))
         setJobs({ ...jobs, rows: updatedRows })
      } catch (error) {
         console.log(error)
      }
      // Mở popup detail
      setSelectedNotification(item)
      setDetailVisible(true)
      setPopoverVisible(false)
   }

   const handleCloseDetail = () => {
      setDetailVisible(false)
      setSelectedNotification(null)
   }

   return (
      <>
         <Popover
            overlayClassName="gx-popover-horizantal popoverNoPadding"
            placement="bottomRight"
            trigger="click"
            visible={isPopoverVisible}
            onVisibleChange={handleVisibleChange}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 1050 }}
            content={
               <div style={{ width: 340, borderRadius: 12, overflow: 'hidden' }}>
                  <div
                     style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#1a1a1a'
                     }}
                  >
                     Thông báo
                  </div>
                  {jobs?.rows?.length ? (
                     <InfiniteScroll
                        dataLength={jobs?.rows?.length ?? 0}
                        next={handleFetchJobs}
                        hasMore={jobs?.rows?.length < (jobs?.count || 0)}
                        loader={<></>}
                        style={{ borderRadius: 0 }}
                        height={420}
                        scrollableTarget="notiScrollTarget"
                     >
                        <ul id="notiScrollTarget" style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
                           {jobs?.rows?.map((job: any, index: number) => (
                              <NotificationItem key={index} isRead={job?.markRead} onClick={() => handleItemClick(job)}>
                                 <div style={{ display: 'flex', gap: 8 }}>
                                    {!job?.markRead && <UnreadDot />}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                       {job?.title && <NotificationTitle>{job.title}</NotificationTitle>}
                                       <NotificationDesc>
                                          <ReactMarkdown className="gx-mb-0">{job?.description}</ReactMarkdown>
                                       </NotificationDesc>
                                       <NotificationTime>
                                          {moment(job?.createdAt).format(DATE_TIME_FORMAT_CLIENT_NEW)}
                                       </NotificationTime>
                                    </div>
                                 </div>
                              </NotificationItem>
                           ))}
                        </ul>
                     </InfiniteScroll>
                  ) : (
                     <div style={{ padding: '24px 0' }}>
                        <NoData />
                     </div>
                  )}
               </div>
            }
         >
            <span
               className="gx-avatar-name gx-mt-auto gx-mb-auto gx-pointer gx-status-pos gx-d-block"
               style={{ height: 22, width: 22 }}
               onClick={handlePopoverClick}
            >
               <img src={Images.ic_bell} alt="notification" />
               {isNewNotif && (
                  <span
                     className="gx-status gx-small gx-orange"
                     style={{ top: 0, left: '12px', backgroundColor: '#ff0000' }}
                  />
               )}
            </span>
         </Popover>

         {/* Popup chi tiết thông báo */}
         <Modal
            visible={detailVisible}
            onCancel={handleCloseDetail}
            footer={null}
            centered
            bodyStyle={{ padding: 0 }}
            width={'70%'}
            destroyOnClose
         >
            {selectedNotification && (
               <div style={{ padding: 24 }}>
                  {selectedNotification.title && (
                     <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
                        {selectedNotification.title}
                     </h3>
                  )}
                  <div style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>
                     {moment(selectedNotification.createdAt).format(DATE_TIME_FORMAT_CLIENT_NEW)}
                  </div>
                  {selectedNotification.image && (
                     <div style={{ marginBottom: 16, textAlign: 'center' }}>
                        <img
                           src={`${CONFIG_URL.IMAGE_URL}/${selectedNotification.image}`}
                           alt="notification"
                           style={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 8,
                              objectFit: 'contain'
                           }}
                        />
                     </div>
                  )}
                  <div style={{ fontSize: 18, lineHeight: '22px', color: '#333' }}>
                     <ReactMarkdown>{selectedNotification.description}</ReactMarkdown>
                  </div>
               </div>
            )}
         </Modal>
      </>
   )
})

export default UserNotification
